import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { reportsApi } from '@/api/reports';
import { ArrowLeft } from '@/components/icons';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import StandaloneReportPreview from '@/components/preview/standalone/StandaloneReportPreview';
import FeatureUnavailableChip from '@/components/subscription/FeatureUnavailableChip';
import { EmptyState } from '@/components/system';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useModal } from '@/hooks/use-modal';
import { ReportPage } from '../constants';
import {
    CreateReportLocationState,
    TemplatePreviewLocationState,
    buildReportDataFromTemplate,
    buildReportDataFromTemplatePreview,
} from './utils/template';

export default function TemplatePreviewPage() {
    const { templateId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { openModal, closeModal } = useModal();
    const previewState = location.state as TemplatePreviewLocationState | undefined;
    const platformTemplateAccess = useFeatureEntitlement('platformTemplates.use');
    const availablePlanName = platformTemplateAccess.availableFromPlan?.name ?? 'ideal';

    const { data: templates = [], isLoading } = useQuery({
        queryKey: ['report-templates'],
        queryFn: reportsApi.getTemplates,
    });

    const template = templates.find((item) => item.id === templateId);
    const isPlatformTemplate = template?.source === 'laudopsi';
    const isLockedPlatformTemplate = isPlatformTemplate && platformTemplateAccess.shouldBlock;
    const shouldLoadFullTemplate = Boolean(
        templateId &&
        isPlatformTemplate &&
        !isLockedPlatformTemplate &&
        !platformTemplateAccess.isLoading,
    );
    const {
        data: fullTemplate,
        isLoading: isFullTemplateLoading,
        isError: isFullTemplateError,
    } = useQuery({
        queryKey: ['report-template', templateId],
        queryFn: () => reportsApi.getTemplate(templateId!),
        enabled: shouldLoadFullTemplate,
        retry: false,
    });
    const previewData = useMemo(() => {
        if (!template) return null;
        if (isLockedPlatformTemplate || isFullTemplateError) {
            return buildReportDataFromTemplatePreview(template);
        }

        const resolvedTemplate = fullTemplate ?? template;
        if (isPlatformTemplate && !fullTemplate) return null;

        return buildReportDataFromTemplate(resolvedTemplate);
    }, [fullTemplate, isFullTemplateError, isLockedPlatformTemplate, isPlatformTemplate, template]);

    const handleBack = () => {
        const returnTo = previewState?.returnTo;

        if (returnTo) {
            navigate(
                {
                    pathname: returnTo.pathname,
                    search: returnTo.search || '',
                },
                {
                    state: returnTo.state,
                },
            );
            return;
        }

        navigate(-1);
    };

    const handleOpenPlans = () => {
        openModal({
            title: 'Desbloquear templates',
            content: (
                <SubscriptionPlansModal
                    close={closeModal}
                    description={`Este modelo está disponível no plano ${availablePlanName}. Escolha um plano para desbloquear templates prontos e aplicar a estrutura completa ao laudo.`}
                />
            ),
            size: 'xl',
        });
    };

    const handleUseTemplate = () => {
        const returnTo = previewState?.returnTo;

        if (isLockedPlatformTemplate || isFullTemplateError) {
            handleOpenPlans();
            return;
        }

        if (template && returnTo) {
            navigate(
                {
                    pathname: returnTo.pathname,
                    search: returnTo.search || '',
                },
                {
                    state: {
                        ...(returnTo.state || {}),
                        pendingTemplateId: template.id,
                    } as CreateReportLocationState,
                },
            );
            return;
        }

        if (template) {
            navigate(`/${ReportPage}`, {
                state: {
                    pendingTemplateId: template.id,
                } as CreateReportLocationState,
            });
            return;
        }

        navigate(-1);
    };

    if (isLoading || isFullTemplateLoading) return <Loading />;

    if (!template) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-surface p-4">
                <EmptyState
                    title="Template não encontrado"
                    description="Não foi possível carregar o template solicitado."
                    action={
                        <Button onClick={handleBack} variant="outline">
                            Voltar
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!previewData) return <Loading />;

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-surface">
            <header className="sticky top-0 z-50 border-b border-border bg-card/95 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/80">
                <div className="mx-auto grid max-w-7xl grid-cols-[auto_1fr_auto] items-center gap-4 px-6 py-4">
                    <Button variant="outline" onClick={handleBack}>
                        <ArrowLeft />
                        Voltar
                    </Button>

                    <div className="min-w-0 text-center">
                        <h1 className="truncate text-lg font-semibold text-foreground md:text-xl">
                            {template.name}
                        </h1>
                        {template.source === 'laudopsi' && (
                            <p className="mt-1 text-sm text-muted-foreground">
                                Criado por LaudoPsi
                            </p>
                        )}
                    </div>

                    <Button
                        onClick={handleUseTemplate}
                        aria-disabled={isLockedPlatformTemplate || isFullTemplateError}
                    >
                        {isLockedPlatformTemplate || isFullTemplateError
                            ? 'Desbloquear template'
                            : 'Usar esse template'}
                        {template.source === 'laudopsi' &&
                        platformTemplateAccess.isUnavailableInPlan &&
                        platformTemplateAccess.availableFromPlan ? (
                            <FeatureUnavailableChip
                                availablePlan={platformTemplateAccess.availableFromPlan}
                            />
                        ) : null}
                    </Button>
                </div>
            </header>

            <div className="min-h-0 flex-1 overflow-auto bg-muted pb-12" data-preview-container>
                <div className="flex justify-center p-8">
                    <div
                        className={
                            isLockedPlatformTemplate || isFullTemplateError
                                ? 'relative max-h-[980px] overflow-hidden'
                                : undefined
                        }
                        data-report-zoom="1"
                    >
                        <StandaloneReportPreview data={previewData} />
                        {isLockedPlatformTemplate || isFullTemplateError ? (
                            <div className="absolute inset-x-0 bottom-0 flex h-2/3 items-end justify-center bg-gradient-to-b from-white/5 via-white/80 to-card p-8 backdrop-blur-[1.5px]">
                                <div className="max-w-md rounded-3xl border border-brand/15 bg-white/95 px-6 py-5 text-center shadow-2xl shadow-brand/10">
                                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-brand">
                                        Prévia limitada
                                    </p>
                                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                                        Este modelo está disponível no plano {availablePlanName}.
                                        Atualize seu plano para aplicar a estrutura completa ao
                                        laudo.
                                    </p>
                                    <div className="mt-4 flex justify-center">
                                        <Button onClick={handleOpenPlans}>Ver planos</Button>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
}
